const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const SCRIPT_LOAD_TIMEOUT_MS = 10_000;

type ScriptLoadState =
    | { status: 'idle' }
    | { status: 'loading'; promise: Promise<void> }
    | { status: 'loaded' }
    | { status: 'failed'; error: Error };

let scriptLoadState: ScriptLoadState = { status: 'idle' };

function failedState(error: Error): Promise<never> {
    scriptLoadState = { status: 'failed', error };
    return Promise.reject(error);
}

/**
 * Load the single shared Turnstile API script and retain its terminal state across consumers.
 * Interactive challenges leave a failure cached so a remount can immediately offer its
 * unavailable path. Background token minting may explicitly retry the failed network load.
 */
export function loadTurnstileScript({ retryAfterFailure = false } = {}): Promise<void> {
    if (window.turnstile) {
        scriptLoadState = { status: 'loaded' };
        return Promise.resolve();
    }

    if (scriptLoadState.status === 'loaded') {
        const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
        existingScript?.remove();
        return failedState(new Error('Turnstile script loaded but API is unavailable'));
    }

    if (scriptLoadState.status === 'loading') {
        return scriptLoadState.promise;
    }

    if (scriptLoadState.status === 'failed') {
        if (!retryAfterFailure) {
            return Promise.reject(scriptLoadState.error);
        }

        document.getElementById(TURNSTILE_SCRIPT_ID)?.remove();
        scriptLoadState = { status: 'idle' };
    }

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript?.dataset.turnstileLoadState === 'failed') {
        const error = new Error('Turnstile script previously failed to load');
        existingScript.remove();
        return failedState(error);
    }

    if (existingScript?.dataset.loaded === 'true' || existingScript?.dataset.turnstileLoadState === 'loaded') {
        existingScript.remove();
        return failedState(new Error('Turnstile script loaded but API is unavailable'));
    }

    const script = existingScript ?? document.createElement('script');
    const attempt = new Promise<void>((resolve, reject) => {
        let settled = false;
        let timeout: ReturnType<typeof setTimeout> | undefined;

        const cleanup = () => {
            if (timeout !== undefined) clearTimeout(timeout);
            script.removeEventListener('load', handleLoad);
            script.removeEventListener('error', handleError);
        };

        const finish = (error?: Error) => {
            if (settled) return;
            settled = true;
            cleanup();

            if (error || !window.turnstile) {
                const loadError = error ?? new Error('Turnstile script loaded but API is unavailable');
                script.dataset.turnstileLoadState = 'failed';
                script.remove();
                scriptLoadState = { status: 'failed', error: loadError };
                reject(loadError);
                return;
            }

            script.dataset.loaded = 'true';
            script.dataset.turnstileLoadState = 'loaded';
            scriptLoadState = { status: 'loaded' };
            resolve();
        };

        function handleLoad() {
            finish();
        }

        function handleError() {
            finish(new Error('Turnstile script failed to load'));
        }

        script.dataset.turnstileLoadState = 'loading';
        script.addEventListener('load', handleLoad);
        script.addEventListener('error', handleError);
        timeout = setTimeout(() => finish(new Error('Turnstile script load timed out')), SCRIPT_LOAD_TIMEOUT_MS);

        if (!existingScript) {
            script.id = TURNSTILE_SCRIPT_ID;
            script.src = `${TURNSTILE_SCRIPT_SRC}?render=explicit`;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }
    });

    scriptLoadState = { status: 'loading', promise: attempt };
    return attempt;
}

/** Reset the persistent loader state between component tests. */
export function resetTurnstileScriptLoaderForTests(): void {
    scriptLoadState = { status: 'idle' };
    document.getElementById(TURNSTILE_SCRIPT_ID)?.remove();
}
